import { Link, useParams } from 'react-router-dom';
import SiteLayout from '@/components/feature/SiteLayout';
import Breadcrumb from '@/components/base/Breadcrumb';
import ShareBar from '@/components/feature/ShareBar';
import EmptyState from '@/components/base/EmptyState';
import { usePageMeta } from '@/hooks/usePageMeta';
import { getNewsById, getRelatedNews, formatDateCN, formatDate } from '@/lib/content';

export default function NewsDetailPage() {
  const { id } = useParams();
  const item = getNewsById(id);

  usePageMeta({
    title: item ? `${item.title} - 琉璃神社` : '资讯不存在 - 琉璃神社',
    description: item ? item.summary : '该条动漫资讯不存在或已被删除，返回琉璃神社资讯首页查看更多内容。',
    keywords: item ? `${item.tags.join(',')},动漫资讯,琉璃神社` : '琉璃神社,动漫资讯',
  });

  if (!item) {
    return (
      <SiteLayout>
        <div className="shell py-16 md:py-24">
          <Breadcrumb items={[{ label: '动漫资讯', to: '/category/news' }, { label: '资讯不存在' }]} />
          <div className="mt-8">
            <EmptyState
              icon="ri-error-warning-line"
              title="资讯不存在或已删除"
              description="这条资讯可能已经下线，或者链接地址有误。你可以回到资讯分类，看看最新的动漫动态。"
              actionLabel="返回资讯列表"
              actionTo="/category/news"
            />
          </div>
        </div>
      </SiteLayout>
    );
  }

  const related = getRelatedNews(item.id, 3);
  const paragraphs = item.content;

  return (
    <SiteLayout>
      <div className="w-full bg-background-100 border-b border-background-200/70">
        <div className="w-full px-4 md:px-8 py-8 md:py-10">
          <Breadcrumb
            items={[
              { label: '动漫资讯', to: '/category/news' },
              { label: item.section, to: '/category/news' },
              { label: '正文' },
            ]}
          />
          <div className="mt-5 max-w-4xl">
            <Link
              to="/category/news"
              className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded bg-primary-50 text-primary-700 text-[11px] hover:bg-primary-100 transition-colors whitespace-nowrap cursor-pointer"
            >
              <i className="ri-bookmark-line w-3.5 h-3.5 flex items-center justify-center"></i>
              {item.section}
            </Link>
            <h1 className="mt-4 text-2xl md:text-[36px] md:leading-[1.35] font-semibold tracking-wide">
              {item.title}
            </h1>
            <p className="mt-4 text-sm md:text-base text-secondary-600 leading-7">{item.summary}</p>
            <div className="mt-5 flex flex-col md:flex-row md:items-center md:justify-between gap-4">
              <div className="flex flex-wrap items-center gap-x-3 gap-y-1.5 text-[11px] md:text-xs text-secondary-500 font-label">
                <span>{formatDateCN(item.date)}</span>
                <span className="w-1 h-1 rounded-full bg-background-400"></span>
                <span>{item.author}</span>
                <span className="w-1 h-1 rounded-full bg-background-400"></span>
                <span className="inline-flex items-center gap-1">
                  <i className="ri-eye-line w-3.5 h-3.5 flex items-center justify-center"></i>
                  {item.views.toLocaleString('en-US')} 阅读
                </span>
              </div>
              <ShareBar title={item.title} />
            </div>
          </div>
        </div>
      </div>

      <div className="shell py-8 md:py-12">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-10">
          <article className="lg:col-span-8">
            <div className="w-full aspect-[16/9] rounded-lg overflow-hidden bg-background-200 border border-background-200/80">
              <img
                src={item.cover}
                alt={item.title}
                title={`${item.title} - 琉璃神社动漫资讯`}
                className="w-full h-full object-cover object-top"
              />
            </div>
            <p className="mt-2.5 text-[11px] text-secondary-400 text-center">
              配图 · 琉璃神社资料库（演示图片）
            </p>

            <div className="mt-7">
              {paragraphs.map((paragraph, index) => (
                <div key={paragraph.slice(0, 12)}>
                  <p className="text-[15px] md:text-base text-foreground-700 leading-[1.95] mb-5">
                    {paragraph}
                  </p>
                  {index === 0 && (
                    <blockquote className="my-7 border-l-2 border-accent-500 bg-background-100/80 rounded-r-md px-5 py-4">
                      <p className="font-heading text-base md:text-lg text-foreground-900 leading-8">
                        {item.quote}
                      </p>
                      <p className="mt-2 text-[11px] text-secondary-500 tracking-wide">
                        — 琉璃神社编辑手记
                      </p>
                    </blockquote>
                  )}
                </div>
              ))}
            </div>

            <div className="mt-8 flex flex-wrap items-center gap-2">
              <span className="text-[11px] tracking-[0.16em] uppercase text-secondary-500 font-label mr-1">
                Tags
              </span>
              {item.tags.map((tag) => (
                <Link
                  key={tag}
                  to={`/search?q=${encodeURIComponent(tag)}`}
                  className="px-2.5 py-1 rounded bg-secondary-100 text-[11px] text-secondary-700 hover:bg-primary-50 hover:text-primary-700 transition-colors whitespace-nowrap cursor-pointer"
                >
                  #{tag}
                </Link>
              ))}
            </div>

            <div className="mt-8 pt-6 border-t border-background-200/70 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
              <Link
                to="/category/news"
                className="inline-flex items-center gap-2 text-sm text-primary-600 hover:text-accent-600 transition-colors whitespace-nowrap cursor-pointer"
              >
                <i className="ri-arrow-left-line w-4 h-4 flex items-center justify-center"></i>
                返回动漫资讯
              </Link>
              <ShareBar title={item.title} />
            </div>
          </article>

          <aside className="lg:col-span-4">
            <div className="lg:sticky lg:top-24 space-y-5">
              <div className="rounded-lg border border-background-200/80 bg-background-50 p-5">
                <div className="flex items-center justify-between">
                  <h2 className="text-sm font-semibold">相关推荐</h2>
                  <span className="text-[11px] text-secondary-400 font-label">{item.section}</span>
                </div>
                <div className="mt-4 space-y-4">
                  {related.map((rel) => (
                    <Link
                      key={rel.id}
                      to={`/detail/news/${rel.id}`}
                      className="group flex gap-3 cursor-pointer"
                    >
                      <span className="shrink-0 w-[84px] h-[62px] rounded-md overflow-hidden bg-background-200">
                        <img
                          src={rel.cover}
                          alt={rel.title}
                          title={`${rel.title} - 琉璃神社动漫资讯`}
                          className="w-full h-full object-cover object-top"
                        />
                      </span>
                      <span className="min-w-0 flex-1">
                        <span className="text-[11px] text-secondary-500 font-label">{formatDate(rel.date)}</span>
                        <span className="mt-1 block text-xs leading-5 text-foreground-800 group-hover:text-primary-700 transition-colors clamp-2">
                          {rel.title}
                        </span>
                      </span>
                    </Link>
                  ))}
                </div>
              </div>

              <div className="rounded-lg border border-background-200/80 bg-background-100/60 p-5">
                <h2 className="text-sm font-semibold">所属分类</h2>
                <div className="mt-3 flex flex-col gap-2">
                  <Link
                    to="/category/news"
                    className="flex items-center justify-between px-3 py-2.5 rounded-md bg-background-50 border border-background-200/70 text-xs text-foreground-700 hover:border-primary-300 hover:text-primary-700 transition-colors cursor-pointer"
                  >
                    动漫资讯
                    <i className="ri-arrow-right-line w-4 h-4 flex items-center justify-center"></i>
                  </Link>
                  <Link
                    to="/category/works"
                    className="flex items-center justify-between px-3 py-2.5 rounded-md bg-background-50 border border-background-200/70 text-xs text-foreground-700 hover:border-primary-300 hover:text-primary-700 transition-colors cursor-pointer"
                  >
                    动漫作品
                    <i className="ri-arrow-right-line w-4 h-4 flex items-center justify-center"></i>
                  </Link>
                  <Link
                    to="/category/culture"
                    className="flex items-center justify-between px-3 py-2.5 rounded-md bg-background-50 border border-background-200/70 text-xs text-foreground-700 hover:border-primary-300 hover:text-primary-700 transition-colors cursor-pointer"
                  >
                    文化专题
                    <i className="ri-arrow-right-line w-4 h-4 flex items-center justify-center"></i>
                  </Link>
                </div>
              </div>
            </div>
          </aside>
        </div>
      </div>
    </SiteLayout>
  );
}